import { UserModel } from "@/types/models";
import { privateApi } from "./api";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from "react-router-dom";

type SignupData = {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    recaptchaToken: string;
};

type LoginData = {
    email: string;
    password: string;
    recaptchaToken: string;
};

type AuthResponse = {
    access_token: string;
};

const signup = async (data: SignupData) => {
    return await privateApi.post('/auth/signup', data);
};

const login = async (data: LoginData) => {
    return await privateApi.post<AuthResponse>('/auth/login', data);
};

const getToken = (): string | null => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    
    if (decoded.exp && decoded.exp < currentTime) {
        localStorage.removeItem('token');
        return null;
    }
    return token;
};

const logout = async (): Promise<void> => {
    localStorage.removeItem('token');
    return Promise.resolve();
};

const getUser = async (): Promise<UserModel> => {
    const response = await privateApi.get<UserModel>('/auth/user');
    return response.data;
};

// Hooks
export const useSignup = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: signup,
        onSuccess: (response) => {
            localStorage.setItem('token', response.data.access_token);
            queryClient.invalidateQueries({ queryKey: ['user'] });
            navigate('/project');
        },
        onError: (error) => {
            console.error("Error while signing up", error);
            throw error;
        },
    });
};

export const useLogin = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: login,
        onSuccess: (response) => {
            localStorage.setItem('token', response.data.access_token);
            queryClient.invalidateQueries({ queryKey: ['user'] });
            navigate('/project');
        },
        onError: (error) => {
            console.error("Error while logging in", error);
            throw error;
        },
    });
};

export const useLogout = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: logout,
        onSuccess: () => {
            // Wipe everything so a different user signing in next doesn't
            // see the previous user's cached projects/samples/images.
            queryClient.clear();
            navigate('/login');
        },
    });
};

export const useGetUser = () => {
    return useQuery({
        queryKey: ['user'],
        queryFn: getUser,
        enabled: !!getToken(),
    });
};

export { getToken };